import React, { useMemo } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import type { ColumnDef } from '@tanstack/react-table'
import { TanStackTable } from '@/@core/components/mui/TanStackTable'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { IUser, UserStatusEnum, useUpdateUserStatusMutation } from '@/@data'
import { UserAvatar } from '../../common/UserAvatar'
import UserStatusDropDown from '../UserStatusDropDown'
import { useNotification } from '@/hooks/useNotification'
import { Button } from '@/@core/components/mui/Button'
import { useRowSelection } from '@/@core/hooks'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import Link from '../../Link'
import { RoleLabel } from '../../role/RoleLabel'
import dayjs from 'dayjs'
import { SelectField } from '../../common/input/SelectField'

interface UserTableProps {
  data: IUser[]
  meta: unknown
  onRowClick?: (employee: IUser) => void
  onEdit?: (employee: IUser) => void
  refetch: () => void
}

export const UserTable: React.FC<UserTableProps> = ({ data, meta, onEdit, refetch }) => {
  const { __ } = useTranslator()
  const { rowSelection, setRowSelection } = useRowSelection(data)
  const changeStatusMutation = useUpdateUserStatusMutation()
  const n = useNotification()
  const { searchQuery } = useRouteQuery()
  const [selectedStatus, setSelectedStatus] = useState<UserStatusEnum>(UserStatusEnum.ACTIVE)

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: 'employee',
        header: __('Lift User'),
        size: 250,
        cell: ({ row }) => {
          return <UserAvatar user={row.original} link={`/cms/users/lift-user/${row.original.id}`} />
        }
      },
      {
        accessorKey: 'role',
        header: __('Role'),
        size: 200,
        cell: ({ row }) => <RoleLabel role={row.original.role} />
      },
      {
        accessorKey: 'company',
        header: __('Company'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {row.original.company?.name}
          </Typography>
        )
      },
      {
        accessorKey: 'created_at',
        header: __('Created Date'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {dayjs(row.original.created_at).format('DD/MM/YYYY')}
          </Typography>
        )
      },
      {
        accessorKey: 'status',
        header: __('Status'),
        cell: ({ row }) => {
          const employee = row.original

          return (
            <UserStatusDropDown
              status={employee.status}
              statusPrevious={employee.status_previous}
              readonly={employee.role_id === 1}
              onSelect={status => {
                changeStatusMutation.mutate(
                  {
                    id: [row.original.id],
                    status
                  },
                  {
                    onSuccess: () => {
                      refetch()
                      n.success(__('Status updated successfully'))
                    },
                    onError: err => {
                      if (err.message) {
                        n.error(__(err.message))

                        return
                      }

                      n.error(__('Status update failed'))
                    }
                  }
                )
              }}
              excludeGuestStatuses={true}
              sx={{
                height: '30px',
                '&.Mui-disabled': { opacity: 1 }
              }}
            />
          )
        }
      },
      {
        accessorKey: 'actions',
        header: __('Actions'),
        cell: ({ row }) => {
          return (
            <Link href={`/cms/users/lift-user/${row.original?.id}`} className='flex items-center gap-4'>
              <i className='tabler-edit text-[#2F2B3DB2] size-[22px]' />
            </Link>
          )
        },
        size: 80
      }
    ],
    [onEdit]
  )

  return (
    <>
      {Object.keys(rowSelection).length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <SelectField
                displayEmpty
                value={Object.keys(rowSelection).length > 0 ? 'Selected' : 'Un-Selected'}
                onChange={e => {
                  if (e.target.value === 'Un-Selected') {
                    setRowSelection({})
                  }
                }}
                options={[
                  { label: Object.keys(rowSelection).length + ' ' + __('Selected'), value: 'Selected' },
                  { label: __('Un-Selected'), value: 'Un-Selected' }
                ]}
                style={{ minWidth: '140px' }}
              />
              <UserStatusDropDown
                status={selectedStatus}
                onStatusChange={setSelectedStatus}
                excludeGuestStatuses={true}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <Button
                variant='contained'
                size='small'
                startIcon={<i className='tabler-check' />}
                sx={{ height: 40 }}
                onClick={() => {
                  if (Object.keys(rowSelection).length > 0) {
                    changeStatusMutation.mutate(
                      {
                        id: Object.keys(rowSelection).map(Number),
                        status: selectedStatus
                      },
                      {
                        onSuccess: () => {
                          refetch()
                          n.success(__('Status updated successfully'))
                        },
                        onError: () => {
                          n.error(__('Status update failed'))
                        }
                      }
                    )
                  }
                }}
                disabled={Object.keys(rowSelection).length === 0}
              >
                {__('Submit')}
              </Button>
              <Button
                variant='tonal'
                color='secondary'
                onClick={() => setRowSelection({})}
                startIcon={<i className='tabler-x' />}
              >
                {__('Clear')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <TanStackTable
        columns={columns}
        tableData={data}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        enableRowSelection
        borderRight='2px solid #e0e0e0'
      />

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1,
              perPage: Number(searchQuery.get('perPage')) || 10
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
